import React, { useState } from "react";
import { toast } from "sonner";

import FileChooser from "./FileChooser";
import { NormalParagraph, Heading4, XsmallText } from "../../lib/ui/components/typography/Typography";
import Button from "../../lib/ui/components/button/Button";
import Modal from "../../lib/ui/components/Modal";
import { UPLOAD_CANDIDATE_TO_JOB } from "../../lib/urls";

export default function UploadApplicantsModal({
    isOpen,
    onClose,
    companyId,
    jobId,
    token,
    onCandidatesUpdate,
}) {
    const [fileName, setFileName] = useState("No file chosen");
    const [file, setFile] = useState(null);

    const handleFileChange = (e) => {
        const file = e.target.files[0];

        if (file) {
            // Check file type on frontend for better UX
            if (!file.name.toLowerCase().endsWith('.csv')) {
                toast.error("Please select a CSV file only.");
                setFile(null);
                setFileName("No file chosen");
                // Clear the input value so user can select the same file again
                e.target.value = '';
                return;
            }

            setFile(file);
            setFileName(file.name);
            // Clear the input value so user can select the same file again after processing
            e.target.value = '';
        } else {
            setFile(null);
            setFileName("No file chosen");
        }
    };

    const uploadFileToBackend = async () => {
        if (!file) return null;

        const formData = new FormData();
        formData.append("file", file);

        try {
            const response = await fetch(
                UPLOAD_CANDIDATE_TO_JOB(companyId, jobId),
                {
                    method: "POST",
                    body: formData,
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (!response?.ok) {
                const error = await response.json();
                const errorMessage = error.detail || "Error uploading file";
                toast.error(errorMessage);

                // Clear the file state when upload fails
                setFile(null);
                setFileName("No file chosen");

                throw new Error(errorMessage);
            }
            const data = await response.json();
            // Refresh candidates after successful upload
            if (onCandidatesUpdate) {
                onCandidatesUpdate();
            }
            return data;
        } catch (error) {
            console.error("Error uploading candidates", error);
            throw error;
        }
    };

    const handleUpload = async () => {
        if (file) {
            try {
                // Upload file immediately with validation
                const data = await uploadFileToBackend();
                // Clear the file state after successful upload
                setFile(null);
                setFileName("No file chosen");
                // Close the modal
                onClose();
                // Show success message with new applicant count
                let message = "File uploaded successfully!";
                if (data?.added > 0) {
                    message = `File uploaded successfully! ${data.added} new applicant${data.added !== 1 ? 's' : ''} added.`;
                } else {
                    message = `File uploaded successfully! All applicants were already in the system.`;
                }
                toast.success(message);
            } catch (error) {
                // Error handling is done in uploadFileToBackend
                console.error("Error uploading file:", error);
            }
        } else {
            toast.error("Please select a file first.");
        }
    };

    const handleClose = () => {
        // Reset file state when closing
        setFile(null);
        setFileName("No file chosen");
        onClose();
    };

    return (
        <Modal show={isOpen} onClose={handleClose} size="2xl">
            <div className="px-8 py-12">
                <div className="mb-6">
                    <Heading4>
                        Add Applicants
                    </Heading4>
                </div>

                <div className="space-y-6 mt-6">
                    <NormalParagraph>
                        Upload a CSV file with the applicants you want to screen. The file
                        should include two columns: <strong>Name</strong> and{" "}
                        <strong>Phone</strong>
                    </NormalParagraph>
                </div>
                <div className="my-5">
                    <FileChooser
                        handleFileChange={handleFileChange}
                        disabled={!file}
                        fileName={fileName}
                    />
                    <XsmallText weight="medium" className="text-gray-500 mt-4">
                        Only .CSV files are supported
                    </XsmallText>
                </div>

                <div className="flex justify-end gap-2 mt-10">
                    <Button
                        variant="secondary"
                        onClick={handleClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        id="upload-csv-button"
                        variant="primary"
                        onClick={handleUpload}
                        disabled={!file}
                    >
                        Upload
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
